/**
 * External Imports
 */
import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { differenceInYears } from 'date-fns';

/**
 * Nexus Imports
 */
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import { logTableStart } from '../../../utils/analyticsHelpers';
import { calculateStayTime } from '../../../utils/helpers';

/**
 * Style Imports
 */
import {
  GoBackBtn,
  GoBackContainer,
  HeaderTitle,
  PatientInfo,
  PatientProfileContainer,
  SubToolbar
} from './PatientProfileHeader.styles';

/**
 * Global Type Definition Imports
 */
import { Patient } from '../../../config/interfaces';

interface IPatientProfileDrawerProps {
  selectedPatient?: Patient | null;
}

const PatientProfileDrawer: FC<IPatientProfileDrawerProps> = ({
  selectedPatient
}) => {
  const navigate = useNavigate();

  const { logTrackingEvent } = useAnalyticsApi();

  const goback = () => {
    logTrackingEvent(logTableStart('/patients/census'));
    navigate(-1);
  };

  const age = useMemo(() => {
    if (!selectedPatient?.dateOfBirth) return '';
    const today = new Date();
    const birthDate = new Date(
      parseInt(selectedPatient.dateOfBirth?.substring(0, 4), 10),
      parseInt(selectedPatient.dateOfBirth?.substring(4, 6), 10) - 1,
      parseInt(selectedPatient.dateOfBirth?.substring(6, 8), 10)
    );
    return differenceInYears(today, birthDate);
  }, [selectedPatient]);

  return (
    <SubToolbar id="patient-profile-drawer">
      <GoBackContainer
        onClick={goback}
        onKeyDown={goback}
        role="button"
        tabIndex={0}
      >
        <GoBackBtn
          color="blue"
          icon="chevron_left"
          kind="tertiary"
          size="small"
        >
          Go Back
        </GoBackBtn>
      </GoBackContainer>
      <PatientProfileContainer>
        <HeaderTitle>
          Last, First
          <PatientInfo>
            {`${selectedPatient?.lastName}, ${selectedPatient?.firstName}`}
          </PatientInfo>
        </HeaderTitle>
        <HeaderTitle>
          MRN
          <PatientInfo>{`${selectedPatient?.mrn}`}</PatientInfo>
        </HeaderTitle>
        <HeaderTitle>
          Age | Sex
          <PatientInfo>
            {age && selectedPatient?.sex
              ? `${age} | ${selectedPatient?.sex}`
              : `N/A`}
          </PatientInfo>
        </HeaderTitle>
        <HeaderTitle>
          Length of Stay
          <PatientInfo>
            {selectedPatient?.lengthOfStay !== -1
              ? `\n${calculateStayTime(selectedPatient?.lengthOfStay || 0)}`
              : `\nN/A`}
          </PatientInfo>
        </HeaderTitle>
        <HeaderTitle>
          Room
          <PatientInfo>
            {selectedPatient?.room ? `${selectedPatient?.room}` : `N/A`}
          </PatientInfo>
        </HeaderTitle>
        <HeaderTitle>
          Unit
          <PatientInfo>
            {selectedPatient?.unitId ? `${selectedPatient?.unitId}` : `N/A`}
          </PatientInfo>
        </HeaderTitle>
        <HeaderTitle>
          Attending Physician
          <PatientInfo>
            {selectedPatient?.attendingPhysician
              ? `${selectedPatient?.attendingPhysician}`
              : `N/A`}
          </PatientInfo>
        </HeaderTitle>
        <HeaderTitle>
          Chief Complaint
          <PatientInfo>
            {selectedPatient?.chiefComplaint
              ? `${selectedPatient?.chiefComplaint}`
              : `N/A`}
          </PatientInfo>
        </HeaderTitle>
      </PatientProfileContainer>
    </SubToolbar>
  );
};

export default PatientProfileDrawer;
